import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import {
  ArticleSchema,
  WebPageSchema,
  BreadcrumbListSchema,
} from "@/components/base/StructuredData";
import { chapters, novelInfo } from "@/mocks/novel";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const chapterId = parseInt(id || "1", 10);

  const chapter = useMemo(
    () => chapters.find((ch) => ch.id === chapterId),
    [chapterId]
  );

  const prevChapter = useMemo(
    () => chapters.find((ch) => ch.id === chapterId - 1),
    [chapterId]
  );

  const nextChapter = useMemo(
    () => chapters.find((ch) => ch.id === chapterId + 1),
    [chapterId]
  );

  if (!chapter) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center py-20">
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                章节未找到
              </h1>
              <p className="text-foreground-500 text-sm mb-6">
                抱歉，该章节不存在或已被移除。
              </p>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors"
              >
                <i className="ri-home-line mr-2"></i>返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${chapter.title} - ${novelInfo.title} | 书香阁`;
  const pageUrl = `https://shuxiangge.com/article/${chapter.id}`;

  return (
    <>
      <ScrollToTop />
      <WebPageSchema
        title={pageTitle}
        description={`${novelInfo.title} ${chapter.title}，作者${novelInfo.author}。${chapter.content.slice(0, 100)}...`}
        url={pageUrl}
        datePublished={chapter.publishDate}
        dateModified="2026-08-07"
      />
      <ArticleSchema
        title={chapter.title}
        description={`${novelInfo.title} ${chapter.title}，作者${novelInfo.author}`}
        url={pageUrl}
        datePublished={chapter.publishDate}
        dateModified="2026-08-07"
        author={novelInfo.author}
      />
      <BreadcrumbListSchema
        items={[
          { name: "首页", url: "https://shuxiangge.com/" },
          { name: novelInfo.title, url: "https://shuxiangge.com/" },
          { name: chapter.title, url: pageUrl },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: novelInfo.title, to: "/" },
                  { label: chapter.title, to: undefined },
                ]}
              />

              <article>
                <header className="mb-10 text-center">
                  <p className="text-xs text-foreground-400 font-medium tracking-widest mb-2">
                    {chapter.volume}
                  </p>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    {chapter.title}
                  </h1>
                  <div className="flex items-center justify-center gap-4 text-xs text-foreground-400">
                    <span>
                      <i className="ri-user-line mr-1"></i>{novelInfo.author} 著
                    </span>
                    <span className="w-px h-3 bg-background-300"></span>
                    <span>
                      <i className="ri-file-text-line mr-1"></i>{chapter.wordCount} 字
                    </span>
                    <span className="w-px h-3 bg-background-300"></span>
                    <span>
                      <i className="ri-calendar-line mr-1"></i>{chapter.publishDate}
                    </span>
                  </div>
                </header>

                <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 md:p-10 mb-10">
                  <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-loose">
                    {chapter.content.split("\n\n").map((paragraph, idx) => (
                      <p key={idx} className="mb-4 text-sm md:text-base leading-relaxed indent-8">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                <nav className="flex items-center justify-between gap-4 mb-16" aria-label="章节导航">
                  {prevChapter ? (
                    <Link
                      to={`/article/${prevChapter.id}`}
                      className="flex items-center gap-2 px-5 py-3 text-sm font-medium bg-background-100 text-foreground-700 rounded-lg border border-background-200/70 hover:border-primary-200 hover:text-primary-500 transition-all whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                      <span className="hidden sm:inline">{prevChapter.title}</span>
                      <span className="sm:hidden">上一章</span>
                    </Link>
                  ) : (
                    <div></div>
                  )}
                  <Link
                    to="/"
                    className="flex items-center gap-2 px-5 py-3 text-sm font-medium bg-background-100 text-foreground-600 rounded-lg border border-background-200/70 hover:border-primary-200 hover:text-primary-500 transition-all whitespace-nowrap"
                  >
                    <i className="ri-home-line"></i>
                    <span className="hidden sm:inline">目录</span>
                  </Link>
                  {nextChapter ? (
                    <Link
                      to={`/article/${nextChapter.id}`}
                      className="flex items-center gap-2 px-5 py-3 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">{nextChapter.title}</span>
                      <span className="sm:hidden">下一章</span>
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                  ) : (
                    <div></div>
                  )}
                </nav>
              </article>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}